import React, { useEffect, useState } from "react";
import {
  Drawer,
  Descriptions,
  Tag,
  Spin,
  Card,
  Space,
  Statistic,
  Row,
  Col,
  Progress,
  Button,
  Divider,
  Table,
  Form,
  Input,
  InputNumber,
  Modal,
  Timeline,
  Collapse,
  Alert,
} from "antd";
import {
  CheckCircleOutlined,
  SendOutlined,
  UnorderedListOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useBudget } from "../../../hooks/finance/useBudget";

const BudgetDetailDrawer = ({
  visible,
  onClose,
  budgetId,
  onSubmit,
  onApprove,
  onReject,
}) => {
  const termLabels = { T1: "Term 1", T2: "Term 2", T3: "Term 3" };
  const {
    budgetDetails,
    loading,
    fetchBudgetDetails,
    addLineItems,
    updateLineItem,
    deleteLineItem,
    updateBudget,
  } = useBudget();

  const [addLineItemModalVisible, setAddLineItemModalVisible] = useState(false);
  const [editLineItemModalVisible, setEditLineItemModalVisible] =
    useState(false);
  const [editBudgetModalVisible, setEditBudgetModalVisible] = useState(false);
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [editingLineItem, setEditingLineItem] = useState(null);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [budgetForm] = Form.useForm();
  const [rejectForm] = Form.useForm();

  useEffect(() => {
    if (visible && budgetId) {
      fetchBudgetDetails(budgetId);
    }
  }, [visible, budgetId]);

  const getStatusColor = (status) => {
    const statusColors = {
      Draft: "default",
      Submitted: "blue",
      Approved: "cyan",
      Active: "green",
      Closed: "gray",
    };
    return statusColors[status] || "default";
  };

  const getReqStatusColor = (status) => {
    const colors = {
      Draft: "default",
      Submitted: "blue",
      "HoD Approved": "cyan",
      "HoD Rejected": "red",
      "Finance Approved": "purple",
      "Finance Rejected": "red",
      Procurement: "orange",
      Fulfilled: "green",
      Cancelled: "default",
    };
    return colors[status] || "default";
  };

  const getPriorityColor = (priority) => {
    const colors = {
      Low: "default",
      Medium: "blue",
      High: "orange",
      Urgent: "red",
    };
    return colors[priority] || "default";
  };

  const formatCurrency = (amount) => {
    return parseFloat(amount || 0).toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  const getUtilizationPercent = () => {
    if (!budgetDetails) return 0;
    const total = parseFloat(budgetDetails.totalAmount) || 0;
    const spent = parseFloat(budgetDetails.amountSpent) || 0;
    const committed = parseFloat(budgetDetails.amountCommitted) || 0;
    return total > 0 ? Math.round(((spent + committed) / total) * 100) : 0;
  };

  const handleAddLineItems = async () => {
    try {
      const values = await form.validateFields();
      const result = await addLineItems(budgetId, [
        {
          category: values.category,
          description: values.description,
          allocatedAmount: values.allocatedAmount,
        },
      ]);
      if (result) {
        form.resetFields();
        setAddLineItemModalVisible(false);
      }
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  const handleEditLineItem = (lineItem) => {
    setEditingLineItem(lineItem);
    editForm.setFieldsValue({
      category: lineItem.category,
      description: lineItem.description,
      allocatedAmount: parseFloat(lineItem.allocatedAmount),
    });
    setEditLineItemModalVisible(true);
  };

  const handleUpdateLineItem = async () => {
    try {
      const values = await editForm.validateFields();
      const result = await updateLineItem(
        budgetId,
        editingLineItem.lineItemId,
        {
          category: values.category,
          description: values.description,
          allocatedAmount: values.allocatedAmount,
        },
      );
      if (result) {
        editForm.resetFields();
        setEditLineItemModalVisible(false);
        setEditingLineItem(null);
      }
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  const handleDeleteLineItem = (lineItem) => {
    Modal.confirm({
      title: "Delete Line Item",
      content: `Are you sure you want to delete "${lineItem.category}"?`,
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        await deleteLineItem(budgetId, lineItem.lineItemId);
      },
    });
  };

  const handleEditBudget = () => {
    budgetForm.setFieldsValue({
      totalAmount: parseFloat(budgetDetails.totalAmount),
      budgetCode: budgetDetails.budgetCode || "",
      notes: budgetDetails.notes || "",
    });
    setEditBudgetModalVisible(true);
  };

  const handleUpdateBudget = async () => {
    try {
      const values = await budgetForm.validateFields();
      const result = await updateBudget(budgetId, {
        totalAmount: values.totalAmount,
        budgetCode: values.budgetCode,
        notes: values.notes,
      });
      if (result) {
        budgetForm.resetFields();
        setEditBudgetModalVisible(false);
        fetchBudgetDetails(budgetId);
      }
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  const requisitionColumns = [
    {
      title: "Req #",
      dataIndex: "requisitionNumber",
      key: "requisitionNumber",
      width: 140,
      render: (num) => (
        <span
          style={{
            fontFamily: "monospace",
            fontWeight: 600,
            color: "#1890ff",
            fontSize: 13,
          }}
        >
          {num}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 130,
      render: (s) => <Tag color={getReqStatusColor(s)}>{s}</Tag>,
    },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      width: 90,
      render: (p) => <Tag color={getPriorityColor(p)}>{p}</Tag>,
    },
    {
      title: "Amount (UGX)",
      dataIndex: "totalAmount",
      key: "totalAmount",
      width: 140,
      align: "right",
      render: (a) => (
        <span style={{ fontWeight: 700, color: "#1890ff" }}>
          {formatCurrency(a)}
        </span>
      ),
    },
    {
      title: "Requested By",
      dataIndex: "requestedByName",
      key: "requestedByName",
      width: 150,
      ellipsis: true,
    },
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 110,
      render: (d) => (d ? dayjs(d).format("DD MMM YYYY") : "—"),
    },
  ];

  const renderLineItems = () => {
    const items = budgetDetails.lineItems || [];
    if (items.length === 0) {
      return (
        <div
          style={{
            textAlign: "center",
            padding: "32px 16px",
            color: "#8c8c8c",
          }}
        >
          <FileTextOutlined
            style={{ fontSize: 32, marginBottom: 8, display: "block" }}
          />
          No line items added yet. Click "Add Line Item" to get started.
        </div>
      );
    }

    const collapseItems = items.map((item) => {
      const available = parseFloat(item.availableBalance || 0);
      const committed = parseFloat(item.committedAmount || 0);
      const spent = parseFloat(item.spentAmount || 0);
      const allocated = parseFloat(item.allocatedAmount || 0);
      const isExhausted = available <= 0;
      const reqs = item.requisitions || [];

      return {
        key: item.lineItemId,
        label: (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              paddingRight: 8,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{ fontWeight: 700, fontSize: 14, lineHeight: "20px" }}
              >
                {item.category}
              </div>
              <div
                style={{
                  color: "#8c8c8c",
                  fontSize: 12,
                  marginTop: 2,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {item.description}
              </div>
            </div>
            <Space
              size={6}
              wrap={false}
              style={{ flexShrink: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              {committed > 0 && (
                <Tag color="orange" style={{ margin: 0, fontSize: 12 }}>
                  {formatCurrency(committed)} committed
                </Tag>
              )}
              {spent > 0 && (
                <Tag color="red" style={{ margin: 0, fontSize: 12 }}>
                  {formatCurrency(spent)} spent
                </Tag>
              )}
              <Tag
                color={isExhausted ? "red" : "green"}
                style={{ margin: 0, fontWeight: 600, fontSize: 12 }}
              >
                {isExhausted
                  ? "Exhausted"
                  : `${formatCurrency(available)} available`}
              </Tag>
              {reqs.length > 0 && (
                <Tag color="blue" style={{ margin: 0, fontSize: 12 }}>
                  {reqs.length} requisition{reqs.length !== 1 ? "s" : ""}
                </Tag>
              )}
              {budgetDetails.status === "Draft" && (
                <Space size={2}>
                  <Button
                    type="text"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditLineItem(item);
                    }}
                  />
                  <Button
                    type="text"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteLineItem(item);
                    }}
                  />
                </Space>
              )}
            </Space>
          </div>
        ),
        children: (
          <div style={{ padding: "4px 0 8px" }}>
            {/* Financial breakdown tiles */}
            <Row
              gutter={[12, 12]}
              style={{ marginBottom: reqs.length > 0 ? 20 : 0 }}
            >
              <Col span={6}>
                <div
                  style={{
                    textAlign: "center",
                    padding: "12px 8px",
                    background: "#e6f4ff",
                    borderRadius: 8,
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      color: "#1890ff",
                      fontWeight: 600,
                      letterSpacing: "0.5px",
                      marginBottom: 6,
                    }}
                  >
                    ALLOCATED
                  </div>
                  <div
                    style={{ fontWeight: 700, color: "#1890ff", fontSize: 15 }}
                  >
                    {formatCurrency(allocated)}
                  </div>
                  <div style={{ fontSize: 11, color: "#8c8c8c" }}>UGX</div>
                </div>
              </Col>
              <Col span={6}>
                <div
                  style={{
                    textAlign: "center",
                    padding: "12px 8px",
                    background: committed > 0 ? "#fff7e6" : "#fafafa",
                    borderRadius: 8,
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      color: committed > 0 ? "#faad14" : "#bfbfbf",
                      fontWeight: 600,
                      letterSpacing: "0.5px",
                      marginBottom: 6,
                    }}
                  >
                    COMMITTED
                  </div>
                  <div
                    style={{
                      fontWeight: 700,
                      color: committed > 0 ? "#faad14" : "#bfbfbf",
                      fontSize: 15,
                    }}
                  >
                    {committed > 0 ? formatCurrency(committed) : "—"}
                  </div>
                  {committed > 0 && (
                    <div style={{ fontSize: 11, color: "#8c8c8c" }}>UGX</div>
                  )}
                </div>
              </Col>
              <Col span={6}>
                <div
                  style={{
                    textAlign: "center",
                    padding: "12px 8px",
                    background: spent > 0 ? "#fff1f0" : "#fafafa",
                    borderRadius: 8,
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      color: spent > 0 ? "#ff4d4f" : "#bfbfbf",
                      fontWeight: 600,
                      letterSpacing: "0.5px",
                      marginBottom: 6,
                    }}
                  >
                    SPENT
                  </div>
                  <div
                    style={{
                      fontWeight: 700,
                      color: spent > 0 ? "#ff4d4f" : "#bfbfbf",
                      fontSize: 15,
                    }}
                  >
                    {spent > 0 ? formatCurrency(spent) : "—"}
                  </div>
                  {spent > 0 && (
                    <div style={{ fontSize: 11, color: "#8c8c8c" }}>UGX</div>
                  )}
                </div>
              </Col>
              <Col span={6}>
                <div
                  style={{
                    textAlign: "center",
                    padding: "12px 8px",
                    background: isExhausted ? "#fff1f0" : "#f6ffed",
                    borderRadius: 8,
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      color: isExhausted ? "#ff4d4f" : "#52c41a",
                      fontWeight: 600,
                      letterSpacing: "0.5px",
                      marginBottom: 6,
                    }}
                  >
                    AVAILABLE
                  </div>
                  <div
                    style={{
                      fontWeight: 700,
                      color: isExhausted ? "#ff4d4f" : "#52c41a",
                      fontSize: 15,
                    }}
                  >
                    {isExhausted ? "Exhausted" : formatCurrency(available)}
                  </div>
                  {!isExhausted && (
                    <div style={{ fontSize: 11, color: "#8c8c8c" }}>UGX</div>
                  )}
                </div>
              </Col>
            </Row>

            {/* Requisitions table */}
            {reqs.length > 0 ? (
              <>
                <Divider style={{ margin: "16px 0 12px" }} />
                <div
                  style={{
                    fontWeight: 600,
                    color: "#262626",
                    marginBottom: 10,
                    fontSize: 13,
                  }}
                >
                  Requisitions charged to this line item
                </div>
                <Table
                  columns={requisitionColumns}
                  dataSource={reqs}
                  rowKey="requisitionId"
                  size="small"
                  pagination={false}
                  scroll={{ x: 700 }}
                  style={{
                    border: "1px solid #f0f0f0",
                    borderRadius: 6,
                    overflow: "hidden",
                  }}
                />
              </>
            ) : (
              <div
                style={{
                  color: "#8c8c8c",
                  fontStyle: "italic",
                  fontSize: 13,
                  textAlign: "center",
                  padding: "8px 0",
                }}
              >
                No requisitions linked to this line item yet.
              </div>
            )}
          </div>
        ),
      };
    });

    return (
      <Collapse
        items={collapseItems}
        bordered={false}
        style={{ background: "transparent" }}
        expandIconPosition="end"
      />
    );
  };

  if (!budgetDetails && !loading) {
    return null;
  }

  const utilizationPercent = getUtilizationPercent();

  return (
    <>
      <Drawer
        title={
          <Space>
            <UnorderedListOutlined />
            <span>Budget Details</span>
            {budgetDetails && (
              <Tag color={getStatusColor(budgetDetails.status)}>
                {budgetDetails.status}
              </Tag>
            )}
          </Space>
        }
        placement="right"
        onClose={onClose}
        open={visible}
        width={900}
        extra={
          budgetDetails && (
            <Space>
              {budgetDetails.status === "Draft" && (
                <>
                  <Button icon={<EditOutlined />} onClick={handleEditBudget}>
                    Edit Budget
                  </Button>
                  <Button
                    type="primary"
                    icon={<SendOutlined />}
                    onClick={() => {
                      onSubmit(budgetDetails);
                      onClose();
                    }}
                  >
                    Submit
                  </Button>
                </>
              )}
              {budgetDetails.status === "Submitted" && (
                <>
                  <Button
                    danger
                    icon={<CloseCircleOutlined />}
                    onClick={() => setRejectModalVisible(true)}
                  >
                    Reject
                  </Button>
                  <Button
                    type="primary"
                    icon={<CheckCircleOutlined />}
                    onClick={() => {
                      onApprove(budgetDetails);
                      onClose();
                    }}
                  >
                    Approve & Activate
                  </Button>
                </>
              )}
              {budgetDetails.status === "Approved" && (
                <Button
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  onClick={() => {
                    onApprove(budgetDetails);
                    onClose();
                  }}
                >
                  Approve & Activate
                </Button>
              )}
            </Space>
          )
        }
      >
        {loading ? (
          <div style={{ textAlign: "center", padding: "50px" }}>
            <Spin size="large" />
          </div>
        ) : (
          budgetDetails && (
            <Space direction="vertical" style={{ width: "100%" }} size="large">
              {/* Financial Summary */}
              <Card title="Financial Summary" size="small">
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <Statistic
                      title="Total Budget"
                      value={formatCurrency(budgetDetails.totalAmount)}
                      prefix="UGX"
                      valueStyle={{ color: "#1890ff" }}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="Available Balance"
                      value={formatCurrency(budgetDetails.availableBalance)}
                      prefix="UGX"
                      valueStyle={{ color: "#52c41a" }}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="Amount Spent"
                      value={formatCurrency(budgetDetails.amountSpent)}
                      prefix="UGX"
                      valueStyle={{ color: "#ff4d4f" }}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="Amount Committed"
                      value={formatCurrency(budgetDetails.amountCommitted)}
                      prefix="UGX"
                      valueStyle={{ color: "#faad14" }}
                    />
                  </Col>
                </Row>
              </Card>

              {/* Utilization */}
              <Card size="small">
                <div style={{ marginBottom: 8 }}>
                  <strong>Budget Utilization</strong>
                  <span
                    style={{ float: "right", color: "#8c8c8c", fontSize: 13 }}
                  >
                    {utilizationPercent}% used
                  </span>
                </div>
                <Progress
                  percent={utilizationPercent}
                  status={
                    utilizationPercent >= 90
                      ? "exception"
                      : utilizationPercent >= 70
                        ? "active"
                        : "normal"
                  }
                  strokeColor={{
                    "0%": "#108ee9",
                    "100%": utilizationPercent >= 90 ? "#ff4d4f" : "#87d068",
                  }}
                />
              </Card>

              {/* Approval Timeline */}
              <Card title="Approval Timeline" size="small">
                <Timeline
                  items={[
                    {
                      color: "green",
                      children: (
                        <div>
                          <div style={{ fontWeight: 600 }}>Budget Created</div>
                          <div style={{ color: "#595959", fontSize: 13 }}>
                            {dayjs(budgetDetails.createdAt).format(
                              "DD MMM YYYY HH:mm",
                            )}
                          </div>
                          <div style={{ color: "#8c8c8c", fontSize: 12 }}>
                            {budgetDetails.departmentName} — FY
                            {budgetDetails.fiscalYear}
                          </div>
                        </div>
                      ),
                    },
                    ...(["Submitted", "Approved", "Active", "Closed"].includes(
                      budgetDetails.status,
                    )
                      ? [
                          {
                            color: "blue",
                            children: (
                              <div>
                                <div style={{ fontWeight: 600 }}>
                                  Submitted for Approval
                                </div>
                                <div style={{ color: "#8c8c8c", fontSize: 12 }}>
                                  Pending finance review
                                </div>
                              </div>
                            ),
                          },
                        ]
                      : []),
                    ...(budgetDetails.approvedAt
                      ? [
                          {
                            color: "green",
                            children: (
                              <div>
                                <div style={{ fontWeight: 600 }}>
                                  {budgetDetails.status === "Approved"
                                    ? "Pending activation (legacy)"
                                    : "Approved & Activated"}
                                </div>
                                <div style={{ color: "#595959", fontSize: 13 }}>
                                  {dayjs(budgetDetails.approvedAt).format(
                                    "DD MMM YYYY HH:mm",
                                  )}
                                </div>
                                {budgetDetails.approvedByName && (
                                  <div
                                    style={{ color: "#8c8c8c", fontSize: 12 }}
                                  >
                                    By {budgetDetails.approvedByName}
                                  </div>
                                )}
                              </div>
                            ),
                          },
                        ]
                      : []),
                    ...(budgetDetails.status === "Closed"
                      ? [
                          {
                            color: "gray",
                            children: (
                              <div>
                                <div style={{ fontWeight: 600 }}>Closed</div>
                                <div style={{ color: "#8c8c8c", fontSize: 12 }}>
                                  Budget period ended
                                </div>
                              </div>
                            ),
                          },
                        ]
                      : []),
                    ...(budgetDetails.status === "Draft"
                      ? [
                          {
                            color: "gray",
                            dot: <ClockCircleOutlined />,
                            children: (
                              <div style={{ color: "#8c8c8c" }}>
                                <div style={{ fontWeight: 600 }}>
                                  Awaiting Submission
                                </div>
                                <div style={{ fontSize: 12 }}>
                                  Add line items and submit for approval
                                </div>
                              </div>
                            ),
                          },
                        ]
                      : []),
                  ]}
                />
              </Card>

              {/* Rejection Remarks */}
              {budgetDetails.status === "Draft" &&
                budgetDetails.rejectionRemarks && (
                  <Alert
                    type="error"
                    showIcon
                    message="Budget Rejected"
                    description={
                      <span>
                        <strong>Reason: </strong>
                        {budgetDetails.rejectionRemarks}
                      </span>
                    }
                  />
                )}

              {/* Budget Information */}
              <Card title="Budget Information" size="small">
                <Descriptions column={2} bordered size="small">
                  <Descriptions.Item label="Item">
                    <strong>{budgetDetails.departmentName}</strong>
                  </Descriptions.Item>
                  <Descriptions.Item label="Fiscal Year">
                    {budgetDetails.fiscalYear}
                  </Descriptions.Item>
                  <Descriptions.Item label="Term">
                    {termLabels[budgetDetails.term] ||
                      budgetDetails.quarter ||
                      "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Budget Code">
                    {budgetDetails.budgetCode || "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Academic Year">
                    {budgetDetails.acadyr}
                  </Descriptions.Item>
                  <Descriptions.Item label="Status">
                    <Tag color={getStatusColor(budgetDetails.status)}>
                      {budgetDetails.status === "Approved"
                        ? "Pending activation (legacy)"
                        : budgetDetails.status}
                    </Tag>
                  </Descriptions.Item>
                  {budgetDetails.approvedByName && (
                    <Descriptions.Item label="Approved By">
                      {budgetDetails.approvedByName}
                    </Descriptions.Item>
                  )}
                  {budgetDetails.approvedAt && (
                    <Descriptions.Item label="Approved At">
                      {dayjs(budgetDetails.approvedAt).format(
                        "DD MMM YYYY HH:mm",
                      )}
                    </Descriptions.Item>
                  )}
                  <Descriptions.Item label="Created">
                    {dayjs(budgetDetails.createdAt).format("DD MMM YYYY HH:mm")}
                  </Descriptions.Item>
                  <Descriptions.Item label="Last Updated">
                    {dayjs(budgetDetails.updatedAt).format("DD MMM YYYY HH:mm")}
                  </Descriptions.Item>
                </Descriptions>
              </Card>

              {/* Notes */}
              {budgetDetails.notes && (
                <Card title="Notes" size="small">
                  <p style={{ whiteSpace: "pre-wrap", margin: 0 }}>
                    {budgetDetails.notes}
                  </p>
                </Card>
              )}

              <Divider />

              {/* Line Items */}
              <Card
                title={
                  <Space>
                    <span>Budget Line Items</span>
                    <Tag>{(budgetDetails.lineItems || []).length} items</Tag>
                  </Space>
                }
                size="small"
                extra={
                  budgetDetails.status === "Draft" && (
                    <Button
                      type="primary"
                      size="small"
                      icon={<PlusOutlined />}
                      onClick={() => setAddLineItemModalVisible(true)}
                    >
                      Add Line Item
                    </Button>
                  )
                }
              >
                {renderLineItems()}
              </Card>
            </Space>
          )
        )}
      </Drawer>

      {/* Add Line Item Modal */}
      <Modal
        title="Add Budget Line Item"
        open={addLineItemModalVisible}
        onCancel={() => {
          form.resetFields();
          setAddLineItemModalVisible(false);
        }}
        onOk={handleAddLineItems}
        confirmLoading={loading}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="category"
            label="Category"
            rules={[{ required: true, message: "Please enter category" }]}
          >
            <Input placeholder="e.g., Supplies, Equipment, Services" />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: "Please enter description" }]}
          >
            <Input.TextArea
              rows={2}
              placeholder="Description of this budget line"
            />
          </Form.Item>
          <Form.Item
            name="allocatedAmount"
            label="Allocated Amount (UGX)"
            rules={[
              { required: true, message: "Please enter amount" },
              { type: "number", min: 0 },
            ]}
          >
            <InputNumber
              style={{ width: "100%" }}
              formatter={(value) =>
                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
              }
              parser={(value) => value.replace(/,/g, "")}
              min={0}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Line Item Modal */}
      <Modal
        title="Edit Budget Line Item"
        open={editLineItemModalVisible}
        onCancel={() => {
          editForm.resetFields();
          setEditLineItemModalVisible(false);
          setEditingLineItem(null);
        }}
        onOk={handleUpdateLineItem}
        confirmLoading={loading}
      >
        <Form form={editForm} layout="vertical">
          <Form.Item
            name="category"
            label="Category"
            rules={[{ required: true, message: "Please enter category" }]}
          >
            <Input placeholder="e.g., Supplies, Equipment, Services" />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: "Please enter description" }]}
          >
            <Input.TextArea
              rows={2}
              placeholder="Description of this budget line"
            />
          </Form.Item>
          <Form.Item
            name="allocatedAmount"
            label="Allocated Amount (UGX)"
            rules={[
              { required: true, message: "Please enter amount" },
              { type: "number", min: 0 },
            ]}
          >
            <InputNumber
              style={{ width: "100%" }}
              formatter={(value) =>
                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
              }
              parser={(value) => value.replace(/,/g, "")}
              min={0}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Budget Modal */}
      <Modal
        title="Edit Budget"
        open={editBudgetModalVisible}
        onCancel={() => {
          budgetForm.resetFields();
          setEditBudgetModalVisible(false);
        }}
        onOk={handleUpdateBudget}
        confirmLoading={loading}
      >
        <Form form={budgetForm} layout="vertical">
          <Form.Item name="budgetCode" label="Budget Code (optional)">
            <Input maxLength={50} placeholder="Enter an optional budget code" />
          </Form.Item>
          <Form.Item
            name="totalAmount"
            label="Total Budget Amount (UGX)"
            rules={[
              { required: true, message: "Please enter total amount" },
              { type: "number", min: 0 },
            ]}
          >
            <InputNumber
              style={{ width: "100%" }}
              formatter={(value) =>
                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
              }
              parser={(value) => value.replace(/,/g, "")}
              min={0}
            />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={3} placeholder="Budget notes" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Reject Budget Modal */}
      <Modal
        title="Reject Budget"
        open={rejectModalVisible}
        okText="Confirm Rejection"
        okButtonProps={{ danger: true }}
        onCancel={() => {
          rejectForm.resetFields();
          setRejectModalVisible(false);
        }}
        onOk={async () => {
          try {
            const values = await rejectForm.validateFields();
            rejectForm.resetFields();
            setRejectModalVisible(false);
            onReject(budgetDetails, values.remarks);
            onClose();
          } catch (_) {}
        }}
      >
        <Form form={rejectForm} layout="vertical">
          <Form.Item
            name="remarks"
            label="Rejection Reason"
            rules={[
              {
                required: true,
                message: "Please provide a reason for rejection",
              },
            ]}
          >
            <Input.TextArea
              rows={4}
              placeholder="Explain why this budget is being rejected..."
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default BudgetDetailDrawer;
